/**
 * "Send it to someone before you decide": the share strip on every personalised product and on
 * Create Your Own (snippets/btf-share-strip.liquid).
 *
 * <btf-share> draws nothing of the design itself. The page's own controller hands it a provider:
 *
 *   share.provider = {
 *     params: () => URLSearchParams,   // the design, as the page's own restore reads it back
 *     tokens: () => ({ words: '…', … }), // fills {name} placeholders in the messages
 *     picture: () => Promise<Blob|null>, // optional: the board as an image, for the picture card
 *     rooms: () => Promise<[{ key, label, canvas, focus }]>  // optional: the board in its rooms
 *   };
 *
 * and shows or hides the strip (the hidden attribute) as there is something of the customer's
 * own to send. Every share goes through share() below, so a later server version (a branded
 * image, emails sent by us) replaces this one method and nothing on the pages.
 *
 * The link is built on the product or page's own address (config.base), never ?variant= from
 * the address bar unless the provider puts it in. A preview theme and a template view in the
 * address travel with it, so a link made while previewing opens on the same preview.
 */

/** The AI server, which keeps a shared picture and gives it a page of its own (a picture link) */
const PICTURE_LINKS = 'https://btf-ai.beyondthefridge.workers.dev/share';

/** Params that belong to the address, not the design: kept as they are */
const CARRY = ['view', 'preview_theme_id'];

/*
 * The picture of the customer's own board. Pinterest, Facebook and link previews read the page
 * from their own servers and only ever see the product photograph, because the design exists
 * only in this browser. So the design travels as a file instead: on a phone the share sheet
 * sends the picture with the link (data-share-to="native"), and on a computer "Save picture"
 * downloads it to add to Pinterest or Facebook by hand.
 *
 * The board comes from the page's provider (provider.picture(), a Blob or null) or, failing
 * that, from the visible board drawing in the section (.btf-pb__art svg: planners, Made in my
 * town, Keep Calm). It is set on a 4:5 card, the shape Pinterest and Instagram show best, with
 * the board's rounded corners and the shop's name underneath. Where the board has a room view,
 * each room is a picture too, and so are the board and rooms together; the customer chooses
 * which to send from thumbnails in the strip. The drawing is in assets/btf-picture.js.
 */
/** The visible board drawing in a section, as an SVG file sized for drawing */
function boardSvg(root) {
  // The main board is the largest drawing showing: the colour swatches are small boards too
  const area = (s) => {
    const r = s.getBoundingClientRect();
    return r.width * r.height;
  };
  const svg = Array.from(root.querySelectorAll('.btf-pb__art svg'))
    .filter((s) => area(s) > 0)
    .sort((a, b) => area(b) - area(a))[0];
  if (!svg) return null;
  const copy = svg.cloneNode(true);
  const vb = (copy.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
  const r = svg.getBoundingClientRect();
  const ratio = vb[2] && vb[3] ? vb[2] / vb[3] : r.width / r.height;
  copy.removeAttribute('style');
  copy.setAttribute('width', '1600');
  copy.setAttribute('height', String(Math.round(1600 / ratio)));
  if (!copy.getAttribute('xmlns')) copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  return new Blob([new XMLSerializer().serializeToString(copy)], { type: 'image/svg+xml' });
}

/**
 * The design params a page was opened with, or null when there are none or a basket line is
 * being edited (assets/btf-edit-line.js restores that instead).
 * @param {string[]} keys
 */
export function sharedDesign(keys) {
  const p = new URLSearchParams(window.location.search);
  if (p.has('edit') || (window.btfEditLine && window.btfEditLine.active)) return null;
  return keys.some((k) => p.has(k)) ? p : null;
}

/*
 * A small "Save" button on each product photograph, as Pinterest's own hover button does, but
 * without Pinterest's script: it opens Pinterest's save page with that photograph (stored on
 * Shopify's CDN, so Pinterest can fetch it) and its own description. The strip's config names
 * the photographs (pinPhotos: a selector, or true for the product gallery).
 */
function pinPhotos(share) {
  const cfg = share.cfg;
  // A named selector may reach photographs in another section (the photo row under a personaliser)
  const root = typeof cfg.pinPhotos === 'string' ? document : share.closest('.shopify-section') || document;
  const selector = typeof cfg.pinPhotos === 'string' ? cfg.pinPhotos : 'media-gallery img.product-media__image';
  const holderOf = (img) => img.closest('.product-media, .btf-pphotos__tile') || img.parentElement;
  const decorate = () => {
    root.querySelectorAll(selector).forEach((img) => {
      const holder = holderOf(img);
      if (!holder || holder.querySelector(':scope > .btf-pin')) return;
      if (getComputedStyle(holder).position === 'static') holder.style.position = 'relative';
      const a = document.createElement('a');
      a.className = 'btf-pin';
      a.target = '_blank';
      a.rel = 'noopener';
      a.href = '#';
      a.textContent = cfg.pinLabel || 'Save';
      a.setAttribute('aria-label', cfg.pinAria || 'Save this photograph to Pinterest');
      a.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        let src = img.currentSrc || img.src;
        try {
          const u = new URL(src, location.href);
          if (u.searchParams.has('width')) u.searchParams.set('width', '1200');
          src = u.toString();
        } catch (err) {
          /* keep the address as it is */
        }
        const description = img.alt || share.text(cfg.message, 'pinterest');
        window.open(
          'https://pinterest.com/pin/create/button/?url=' + encodeURIComponent(share.url('pinterest')) +
            '&media=' + encodeURIComponent(src) +
            '&description=' + encodeURIComponent(description),
          '_blank',
          'noopener'
        );
      });
      holder.appendChild(a);
    });
  };
  decorate();
  let t = 0;
  new MutationObserver(() => {
    clearTimeout(t);
    t = setTimeout(decorate, 300);
  }).observe(root, { childList: true, subtree: true });
}

class BtfShare extends HTMLElement {
  // No field for `provider`: a page's controller may set it before this element is upgraded,
  // and a class field would reset it to empty during the upgrade.

  connectedCallback() {
    if (this.cfg) return;
    try {
      this.cfg = JSON.parse(this.querySelector('script[data-share-config]').textContent);
    } catch (e) {
      this.cfg = {};
    }
    // Phones get one Share button for the phone's own sheet; desktops get the five buttons
    const phone = typeof navigator.share === 'function' && window.matchMedia('(pointer: coarse)').matches;
    this.querySelectorAll('[data-share-phone]').forEach((el) => (el.hidden = !phone));
    this.querySelectorAll('[data-share-desktop]').forEach((el) => (el.hidden = phone));
    this.addEventListener('click', (e) => {
      const btn = e.target instanceof Element && e.target.closest('[data-share-to]');
      if (btn) this.share(btn.dataset.shareTo, btn);
    });
    if (this.cfg.pinPhotos) pinPhotos(this);
    this.watchDesign();
  }

  /** The board as an image (not yet on its card), or null where there is none to draw */
  async boardImage() {
    let board = null;
    if (this.provider && this.provider.picture) board = await this.provider.picture();
    if (!board) board = boardSvg(this.closest('.shopify-section') || document);
    return board;
  }

  /** The board in its room or rooms: [{ key, label, canvas, focus }] */
  async roomImages(P) {
    if (this.provider && this.provider.rooms) return (await this.provider.rooms()) || [];
    // The three-layer boards: the largest showing is the main board
    const root = this.closest('.shopify-section') || document;
    const area = (el) => {
      const r = el.getBoundingClientRect();
      return r.width * r.height;
    };
    const art = Array.from(root.querySelectorAll('.btf-pb__art svg'))
      .filter((el) => area(el) > 0)
      .sort((a, b) => area(b) - area(a))[0];
    const board = art && art.closest('.btf-pb__stage') && art.closest('.btf-pb__stage').parentElement;
    if (!board) return [];
    const room = await P.pbRoom(board);
    return room ? [{ key: 'room', label: this.cfg.roomLabel || 'In a room', ...room }] : [];
  }

  /**
   * Every picture for the design as it stands: the board card first, then each room, then the
   * board and rooms together. Thumbnails appear as each is ready.
   */
  async makePictures(job) {
    const P = await import((window.btfAssets && window.btfAssets.picture) || './btf-picture.js');
    const fresh = () => this._set === job && this.designKey() === job.key;
    const add = (item) => {
      if (!fresh() || !item.blob) return;
      item.url = URL.createObjectURL(item.blob);
      job.items.push(item);
      this.renderPictures();
    };
    const board = await this.boardImage();
    if (!board || !fresh()) return;
    // The page may mark the copy that is sent or saved (the pet portrait's fish); the strip shows
    // the clean one, so only the person it is sent to sees the mark
    const marked = await this.marked(board);
    add({ key: 'board', label: this.cfg.boardLabel || 'Your design', blob: await P.boardCard(board), send: marked ? await P.boardCard(marked) : null });
    let rooms = [];
    try {
      rooms = await this.roomImages(P);
    } catch (e) {
      rooms = [];
    }
    for (const r of rooms) add({ key: r.key, label: r.label, blob: await P.roomCard(r.canvas, r.focus) });
    if (rooms.length) add({ key: 'all', label: this.cfg.allLabel || 'All together', blob: await P.combined(board, rooms), send: marked ? await P.combined(marked, rooms) : null });
    job.done = true;
  }

  /** The thumbnails to choose from, with the chosen ones marked */
  renderPictures() {
    const box = this.querySelector('[data-share-pics]');
    const set = this._set && this._set.items.length ? this._set : this._prev;
    if (!box || !set) return;
    if (!this._chosen || !set.items.some((i) => this._chosen.has(i.key))) this._chosen = new Set(['board']);
    box.hidden = !set.items.length;
    const row = box.querySelector('[data-share-pics-row]') || box;
    row.replaceChildren(
      ...set.items.map((item) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'btf-share__pic';
        b.dataset.pic = item.key;
        b.setAttribute('aria-pressed', this._chosen.has(item.key) ? 'true' : 'false');
        b.title = item.label;
        const img = document.createElement('img');
        img.src = item.url;
        img.alt = '';
        const label = document.createElement('span');
        label.className = 'btf-share__pic-label';
        label.textContent = item.label;
        b.append(img, label);
        return b;
      })
    );
  }

  choose(key) {
    if (!this._chosen) this._chosen = new Set(['board']);
    if (this._chosen.has(key)) {
      if (this._chosen.size > 1) this._chosen.delete(key);
    } else this._chosen.add(key);
    this.renderPictures();
  }

  /** The chosen pictures, ready now, in the order they are shown */
  chosen() {
    const set = this._set;
    if (!set || set.key !== this.designKey()) return [];
    const picked = set.items.filter((i) => this._chosen && this._chosen.has(i.key));
    return picked.length ? picked : set.items.slice(0, 1);
  }

  /** The board as the page marks it for sending, or null where the page adds no mark */
  async marked(board) {
    if (!this.provider || !this.provider.mark) return null;
    try {
      const m = await this.provider.mark(board);
      return m && m !== board ? m : null;
    } catch (e) {
      return null;
    }
  }

  fileFor(item, i) {
    const name = (this.cfg.fileName || 'my-beyond-the-fridge-design') + (item.key === 'board' ? '' : '-' + item.key);
    const blob = item.send || item.blob;
    const type = blob.type || 'image/png';
    return new File([blob], name + (type === 'image/jpeg' ? '.jpg' : '.png'), { type });
  }

  /** The customer's design on its card, or null (kept for the pages that ask for one picture) */
  async picture() {
    if (!this.hasAttribute('data-picture')) return null;
    try {
      const board = await this.boardImage();
      if (!board) return null;
      const P = await import((window.btfAssets && window.btfAssets.picture) || './btf-picture.js');
      return await P.boardCard((await this.marked(board)) || board);
    } catch (e) {
      return null;
    }
  }

  async savePicture(button) {
    let items = this.chosen();
    if (!items.length) {
      const blob = await this.picture();
      if (!blob) return;
      items = [{ key: 'board', blob }];
    }
    items.forEach((item, i) => {
      const file = this.fileFor(item, i);
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    });
    if (button && this.cfg.saved) {
      const label = button.dataset.label || button.textContent;
      button.dataset.label = label;
      button.textContent = this.cfg.saved;
      setTimeout(() => (button.textContent = label), 2400);
    }
  }

  /** The design as it stands, so pictures made earlier are known to still match */
  designKey() {
    const params = this.provider && this.provider.params ? this.provider.params() : null;
    const stamp = this.provider && this.provider.stamp ? this.provider.stamp() : '';
    return (params ? params.toString() : location.search) + '#' + stamp;
  }

  /*
   * A phone only opens its share sheet straight from the tap, so the pictures are made ahead:
   * once the strip is on screen and the customer has paused, and again as a finger touches it.
   */
  prepare() {
    if (!this.hasAttribute('data-picture') || this.hidden) return;
    const key = this.designKey();
    if (this._set && this._set.key === key) return;
    // The thumbnails already showing stay until the new design's pictures replace them
    if (this._set && this._set.items.length) {
      if (this._prev) this._prev.items.forEach((i) => i.url && URL.revokeObjectURL(i.url));
      this._prev = this._set;
    }
    const job = { key, items: [], done: false };
    this._set = job;
    this.makePictures(job).catch(() => {});
  }

  watchDesign() {
    if (!this.hasAttribute('data-picture')) return;
    const root = this.closest('.shopify-section') || document;
    let t = 0;
    const soon = () => {
      clearTimeout(t);
      t = setTimeout(() => this._inView && this.prepare(), 1200);
    };
    root.addEventListener('input', soon);
    root.addEventListener('change', soon);
    // The page redrew the design by itself (the pet portrait's drawing arriving)
    root.addEventListener('btf:redraw', soon);
    // Reframing a photograph (Create Your Own) is a drag, not an input
    root.addEventListener('pointerup', soon);
    // Reaching for the strip makes the pictures, then the picture link, ahead of the tap
    const ready = () => {
      this.prepare();
      this.pageFor();
    };
    this.addEventListener('touchstart', ready, { passive: true });
    this.addEventListener('pointerenter', ready);
    this.addEventListener('focusin', ready);
    this.addEventListener('click', (e) => {
      const pic = e.target instanceof Element && e.target.closest('[data-pic]');
      if (pic) this.choose(pic.dataset.pic);
    });
    // The pictures are only made once the strip comes near the screen
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          this._inView = entries.some((e) => e.isIntersecting);
          if (this._inView) soon();
        },
        { rootMargin: '300px 0px' }
      ).observe(this);
    } else {
      this._inView = true;
      soon();
    }
  }

  /** The phone's own share sheet, with the chosen pictures where the phone takes files */
  shareNative(channel, message, link) {
    const title = this.text(this.cfg.subject, channel);
    const items = this.chosen();
    if (!items.length) this.prepare();
    if (items.length) {
      const files = items.map((item, i) => this.fileFor(item, i));
      if (navigator.canShare && navigator.canShare({ files })) {
        // Some apps drop the url field when a file goes with it, so the link rides in the text too
        return navigator.share({ files, title, text: message + ' ' + link }).catch(() => {});
      }
    }
    return navigator.share({ title, text: message, url: link }).catch(() => {});
  }

  /*
   * The picture link: the board as it is sent (item.send, with any mark) kept on the AI server,
   * which gives it a page on the shop (/apps/btf-ai/s/…) whose preview shows the design itself.
   * Made once per design, when the customer reaches for the strip. Resolves { page, image } or
   * null (no picture here, or the server could not be reached: the plain link is used instead).
   */
  pageFor() {
    if (!this.hasAttribute('data-picture') || this.hidden) return Promise.resolve(null);
    // The design may have changed since the pictures were made (a drawing arriving): make them again
    this.prepare();
    const job = this._set;
    if (!job || job.key !== this.designKey()) return Promise.resolve(null);
    if (job.pageP) return job.pageP;
    job.pageP = (async () => {
      // Wait for the board picture (made a moment after the strip comes into view)
      for (let i = 0; i < 60 && !job.items.length; i++) await new Promise((r) => setTimeout(r, 250));
      const item = job.items.find((x) => x.key === 'board') || job.items[0];
      if (!item) return null;
      const src = item.send || item.blob;
      const bmp = await createImageBitmap(src);
      const k = Math.min(1, 1600 / bmp.width);
      const c = document.createElement('canvas');
      c.width = Math.round(bmp.width * k);
      c.height = Math.round(bmp.height * k);
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(bmp, 0, 0, c.width, c.height);
      const img = c.toDataURL('image/jpeg', 0.86);
      const tokens = this.provider && this.provider.tokens ? this.provider.tokens() : {};
      const r = await fetch(PICTURE_LINKS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ img, w: c.width, h: c.height, title: tokens.design || '', link: this.url('link') }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.page) return null;
      job.page = j;
      return j;
    })().catch(() => null);
    return job.pageP;
  }

  /** The picture link for this design if it is ready, tagged with the channel; else '' */
  pageLink(channel) {
    const job = this._set;
    if (!job || job.key !== this.designKey() || !job.page) return '';
    return job.page.page + '?utm_source=share&utm_medium=' + encodeURIComponent(channel);
  }

  /** The link that opens this design, tagged with the channel it was sent through */
  url(channel) {
    const url = new URL(this.cfg.base || location.origin + location.pathname);
    const here = new URLSearchParams(location.search);
    CARRY.forEach((k) => here.has(k) && url.searchParams.set(k, here.get(k)));
    // Shopify drops preview_theme_id from the address once the preview has started, so an
    // unpublished theme adds its own id. Never on the live theme.
    const theme = window.Shopify && window.Shopify.theme;
    if (!url.searchParams.has('preview_theme_id') && theme && theme.id && theme.role && theme.role !== 'main') {
      url.searchParams.set('preview_theme_id', String(theme.id));
    }
    const params = this.provider && this.provider.params ? this.provider.params() : null;
    if (params) params.forEach((v, k) => url.searchParams.set(k, v));
    url.searchParams.set('utm_source', 'share');
    url.searchParams.set('utm_medium', channel);
    return url.toString();
  }

  text(template, channel, link) {
    const fill = Object.assign({}, this.provider && this.provider.tokens ? this.provider.tokens() : {}, {
      link: link || this.url(channel),
      product: this.cfg.base || location.origin + location.pathname,
    });
    return String(template || '').replace(/\{(\w+)\}/g, (m, k) => (k in fill ? fill[k] : m));
  }

  /** The photograph of the colour or size chosen now (a product page), from the variant map */
  variantImage() {
    const map = this.cfg.variantImages;
    if (!map) return '';
    const root = this.closest('.shopify-section') || document;
    const field = root.querySelector('form[action*="/cart/add"] [name="id"]') || document.querySelector('form[action*="/cart/add"] [name="id"]');
    const id = (field && field.value) || new URLSearchParams(location.search).get('variant');
    return (id && map[id]) || '';
  }

  /** A short line under the buttons, for a moment */
  note(text) {
    if (!text) return;
    let el = this.querySelector('.btf-share__note');
    if (!el) {
      el = document.createElement('p');
      el.className = 'btf-share__note';
      el.setAttribute('role', 'status');
      this.appendChild(el);
    }
    el.textContent = text;
    clearTimeout(this._noteTimer);
    this._noteTimer = setTimeout(() => (el.textContent = ''), 12000);
  }

  /**
   * Sends the design through one channel. Where the page has a picture, the link sent is its
   * picture link (made as the customer reached for the strip, or now): a new window is opened at
   * once, while the tap still allows it, and pointed at the channel when the link is ready.
   */
  async share(channel, button) {
    if (channel === 'save' || !this.hasAttribute('data-picture')) return this.send(channel, button, '');
    let page = this.pageLink(channel);
    if (!page && channel !== 'native') {
      const win = ['whatsapp', 'facebook', 'pinterest'].includes(channel) ? window.open('', '_blank') : null;
      if (win) win.opener = null;
      await Promise.race([this.pageFor(), new Promise((r) => setTimeout(r, 12000))]);
      page = this.pageLink(channel);
      return this.send(channel, button, page, win);
    }
    return this.send(channel, button, page);
  }

  send(channel, button, page, win) {
    const cfg = this.cfg;
    const link = page || this.url(channel);
    const message = this.text(cfg.message, channel, link);
    const open = (u) => (win && !win.closed ? (win.location.href = u) : window.open(u, '_blank', 'noopener'));
    switch (channel) {
      case 'whatsapp':
        open('https://wa.me/?text=' + encodeURIComponent(message + ' ' + link));
        break;
      case 'facebook':
        open('https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(link));
        // A picture link shows the design in Facebook's preview. Without one the preview is the
        // product page's photograph, so the picture is saved and the link copied, to add by hand
        if (this.hasAttribute('data-picture') && !page) {
          if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(link).catch(() => {});
          this.savePicture();
          this.note(cfg.fbNote);
        }
        break;
      case 'pinterest': {
        // Pinterest's own Save window with a real photograph of the product (a lifestyle shot
        // where the page names one), and the link that opens the customer's design. Their own
        // design picture goes by Save picture, or straight from a phone's Share.
        // With a picture link, the pin is the customer's own design
        const job = this._set;
        const image = (page && job && job.page && job.page.image) || this.variantImage() || cfg.pinImage || cfg.image;
        open(
          'https://pinterest.com/pin/create/button/?url=' + encodeURIComponent(link) +
            (image ? '&media=' + encodeURIComponent(image) : '') +
            '&description=' + encodeURIComponent(message)
        );
        break;
      }
      case 'email':
        window.location.href =
          'mailto:?subject=' + encodeURIComponent(this.text(cfg.subject, channel, link)) +
          '&body=' + encodeURIComponent(this.text(cfg.body, channel, link));
        break;
      case 'native':
        if (navigator.share) this.shareNative(channel, message, link);
        break;
      case 'save':
        this.savePicture(button);
        break;
      case 'copy': {
        const done = () => {
          if (!button) return;
          const label = button.dataset.label || button.textContent;
          button.dataset.label = label;
          button.textContent = cfg.copied || label;
          setTimeout(() => (button.textContent = label), 1800);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(link).then(done, () => window.prompt('', link));
        } else window.prompt('', link);
        break;
      }
    }
    return link;
  }
}

if (!customElements.get('btf-share')) customElements.define('btf-share', BtfShare);
